<template>
    <div v-if="hasActiveDirectory && !hasRealtimeOnlyModule" class="relative h-full">
        <DropDown v-model:expand="expandDropdown" dropdown-class="mt-2 w-60">
            <template #dropdown-content>
                <ActionModal :initial-step-index="0" :initial-state="actionModalInitialState">
                    <template #default="{ open }">
                        <div class="py-1">
                            <button
                                type="button"
                                class="text-left w-full px-6 py-3 hover:bg-gray-100 font-medium cursor-pointer first:rounded-t-md"
                                role="menuitem"
                                @click="batchExpanded = !batchExpanded"
                                @keydown.esc="expandDropdown = false"
                            >
                                <div style="grid-template-columns: 2em auto 1.5em" class="w-full grid items-center gap-2">
                                    <div class="grid content-center">
                                        <Icon :src="batchIcon"></Icon>
                                    </div>
                                    <span class="text-gray-800">{{ $t('layouts.dashboard.topBar.batchButton.label') }}</span>
                                    <div class="justify-end grid">
                                        <ExpandIcon color="black" :expand="batchExpanded"></ExpandIcon>
                                    </div>
                                </div>
                            </button>
                            <div v-if="batchExpanded">
                                <button
                                    v-for="event of batchEvents"
                                    :key="event.id"
                                    type="button"
                                    class="text-left w-full pl-10 pr-6 py-2 hover:bg-gray-100 cursor-pointer"
                                    role="menuitem"
                                    @click="onBatchEventClicked(event, open)"
                                    @keydown.esc="expandDropdown = false"
                                >
                                    <div style="grid-template-columns: 2em auto" class="grid gap-2 items-center">
                                        <div class="grid content-center">
                                            <Icon :src="event.icon_url"></Icon>
                                        </div>
                                        <span class="text-gray-800">{{ event.name }}</span>
                                    </div>
                                </button>
                            </div>
                            <button
                                type="button"
                                class="text-left w-full px-6 py-3 hover:bg-gray-100 font-medium cursor-pointer last:rounded-b-md"
                                role="menuitem"
                                @click="onRecalculateClicked"
                                @keydown.esc="expandDropdown = false"
                            >
                                <div style="grid-template-columns: 2em auto" class="w-full grid items-center gap-2">
                                    <div class="grid content-center">
                                        <Icon :src="recalculateIcon" class="grayscale"></Icon>
                                    </div>
                                    <span class="text-gray-800">{{ $t('layouts.dashboard.topBar.updateKeyFigures.buttonLabel') }}</span>
                                </div>
                            </button>
                        </div>
                    </template>
                </ActionModal>
            </template>

            <button
                type="button"
                class="bg-primary-800 border-primary-700 border hover:bg-primary-700 transition-colors rounded-xl py-2 px-4 text-white cursor-pointer h-full"
                @click="expandDropdown = !expandDropdown"
            >
                <span class="w-full flex space-x-2 items-center">
                    <span class="font-semibold text-lg">{{ $t('layouts.dashboard.topBar.actionsMenu.label') }}</span>
                    <ExpandIcon color="white" :expand="expandDropdown"></ExpandIcon>
                </span>
            </button>
        </DropDown>
        <!-- UpdateKeyFigures is mounted hidden here so its Modal slot scope is live.
             The Modal component only exposes `open` via slot scope, so we can't
             drive it from outside without a mounted instance. Calling
             updateKeyFigures.open() clicks a hidden trigger button inside the
             component that has access to that slot scope. -->
        <div class="hidden" aria-hidden="true">
            <UpdateKeyFigures ref="updateKeyFigures"></UpdateKeyFigures>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import DropDown from '@/components/common/DropDown.vue';
import ExpandIcon from '@/components/icons/ExpandIcon.vue';
import Icon from '@/components/icons/Icon.vue';
import ActionModal from '@/components/common/actionModal/ActionModal.vue';
import UpdateKeyFigures from '@/components/layouts/dashboardLayout/topBar/updateKeyFigures/UpdateKeyFigures.vue';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import { BatchEvent, IconSource, MultiStepFormState } from '@/types';

export default defineComponent({
    components: { DropDown, ExpandIcon, Icon, ActionModal, UpdateKeyFigures },
    setup() {
        return {
            batchIcon: IconSource.NavPigsInactive,
            recalculateIcon: IconSource.RefreshActivities,
        };
    },
    data() {
        return {
            expandDropdown: false,
            batchExpanded: false,
            selectedBatchEvent: null as null | BatchEvent,
        };
    },
    computed: {
        hasActiveDirectory(): boolean {
            return store.getters.hasActiveDirectory;
        },
        hasRealtimeOnlyModule(): boolean {
            return store.getters.hasRealtimeOnlyModule;
        },
        batchEvents(): BatchEvent[] {
            return store.state.batchEvents.filter((currentBatchEvent) => currentBatchEvent.is_listed);
        },
        actionModalInitialState(): MultiStepFormState {
            return {
                location: null,
                form: null,
                confirmed: false,
                farm: null,
                record_id: null,
                notification: null,
                event: this.selectedBatchEvent,
            };
        },
    },
    created() {
        store.dispatch(ActionType.GetBatchEvents);
        store.dispatch(ActionType.GetActions);
    },
    methods: {
        onBatchEventClicked(event: BatchEvent, open: CallableFunction) {
            this.selectedBatchEvent = event;
            open();
        },
        onRecalculateClicked() {
            this.expandDropdown = false;
            const trigger = this.$refs.updateKeyFigures as { open: () => void } | undefined;
            trigger?.open();
        },
    },
});
</script>
